import { useState, useEffect } from "react";
import { Video, Loader2, ChevronRight, Clock, Film } from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import { SearchBar, Table } from "../../components/ui";
import { StatusBadge, PlatformBadge } from "./StatusBadge";
import MeetingDrawer from "./MeetingDrawer";
import ZoomClipsDrawer from "./ZoomClipsDrawer";
import ZoomRecordingsDrawer from "./ZoomRecordingsDrawer";
import { fetchMeetings, fmtDate } from "../../services/api";
import { useAuth } from "../../hooks/useAuth";

const STATUS_FILTERS = [
  { id: "", label: "All" },
  { id: "pending-download", label: "Pending" },
  { id: "uploaded", label: "Uploaded" },
  { id: "processing", label: "Processing" },
  { id: "transcribed", label: "Transcribed" },
  { id: "clips-ready", label: "Ready" },
  { id: "error", label: "Error" },
];

const PLATFORM_FILTERS = [
  { id: "", label: "All" },
  { id: "zoom", label: "Zoom" },
  { id: "zoom-webinar", label: "Webinars" },
  { id: "zoom-clip", label: "Zoom Clips" },
  { id: "teams", label: "Teams" },
];

export default function MeetingsList({ embedded = false }) {
  const { user, role } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showZoomClips, setShowZoomClips] = useState(false);
  const [showZoomRecordings, setShowZoomRecordings] = useState(false);

  const loadMeetings = () => {
    setLoading(true);
    setError(null);
    fetchMeetings({ search, status: statusFilter, platform: platformFilter })
      .then((data) => {
        setMeetings(data.meetings || []);
        setTotal(data.total || 0);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const timer = setTimeout(loadMeetings, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [search, statusFilter, platformFilter]);

  const formatDuration = (seconds) => {
    if (!seconds) return "—";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const columns = [
    {
      key: "title",
      label: "Video",
      render: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(99,102,241,0.08)",
            }}
          >
            <Video size={16} color={colors.info} />
          </div>
          <div>
            <div style={{ fontWeight: 600 }}>{row.title}</div>
            {row.hostName && (
              <div style={{ fontSize: 12, color: colors.textSecondary }}>
                {row.hostName}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "platform",
      label: "Platform",
      render: (row) => <PlatformBadge platform={row.platform} />,
    },
    {
      key: "date",
      label: "Date",
      render: (row) => (
        <span style={{ fontFamily: fonts.mono, fontSize: 12 }}>
          {fmtDate(row.recordedAt)}
        </span>
      ),
    },
    {
      key: "duration",
      label: "Duration",
      render: (row) => (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontSize: 12,
            color: colors.textSecondary,
          }}
        >
          <Clock size={12} />
          <span style={{ fontFamily: fonts.mono }}>
            {formatDuration(row.durationSeconds)}
          </span>
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "arrow",
      label: "",
      render: () => <ChevronRight size={14} color={colors.textSecondary} />,
    },
  ];

  return (
    <div>
      {!embedded && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 24,
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: fonts.heading,
                fontSize: 28,
                fontWeight: 700,
                color: colors.text,
                marginBottom: 4,
              }}
            >
              Videos
            </h1>
            <p
              style={{
                fontFamily: fonts.body,
                fontSize: 14,
                color: colors.textSecondary,
              }}
            >
              {loading
                ? "Loading..."
                : `${total} video${total !== 1 ? "s" : ""}`}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder="Search videos..."
            />
          </div>
        </div>
      )}

      {embedded && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder="Search recordings..."
            />
          </div>
          <button
            onClick={() => setShowZoomClips(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              borderRadius: 8,
              border: `1px solid ${colors.accent}`,
              background: colors.accentDim,
              color: colors.accent,
              fontSize: 13,
              fontFamily: fonts.body,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.15s",
              whiteSpace: "nowrap",
            }}
          >
            <Film size={15} />
            Zoom Clips
          </button>
          <button
            onClick={() => setShowZoomRecordings(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              borderRadius: 8,
              border: `1px solid ${colors.accent}`,
              background: colors.accentDim,
              color: colors.accent,
              fontSize: 13,
              fontFamily: fonts.body,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.15s",
              whiteSpace: "nowrap",
            }}
          >
            <Video size={15} />
            Zoom Recordings
          </button>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: "flex", gap: 20, marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setStatusFilter(f.id)}
              style={{
                padding: "6px 12px",
                borderRadius: 6,
                border: `1px solid ${statusFilter === f.id ? colors.accent : colors.border}`,
                background:
                  statusFilter === f.id ? colors.accentDim : "transparent",
                color:
                  statusFilter === f.id ? colors.accent : colors.textSecondary,
                fontSize: 12,
                fontFamily: fonts.body,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: 6 }}>
          {PLATFORM_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setPlatformFilter(f.id)}
              style={{
                padding: "6px 12px",
                borderRadius: 6,
                border: `1px solid ${platformFilter === f.id ? colors.accent : colors.border}`,
                background:
                  platformFilter === f.id ? colors.accentDim : "transparent",
                color:
                  platformFilter === f.id
                    ? colors.accent
                    : colors.textSecondary,
                fontSize: 12,
                fontFamily: fonts.body,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: 16,
            background: "rgba(239,68,68,0.1)",
            border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: 8,
            marginBottom: 16,
            color: "#ef4444",
            fontSize: 13,
          }}
        >
          Failed to load videos: {error}
        </div>
      )}

      {!loading && meetings.length === 0 && !error ? (
        <div
          style={{
            textAlign: "center",
            padding: 48,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            background: colors.bgCard,
          }}
        >
          <Video
            size={32}
            color={colors.textSecondary}
            style={{ marginBottom: 12 }}
          />
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: colors.text,
              marginBottom: 6,
            }}
          >
            No videos yet
          </div>
          <div
            style={{
              fontSize: 13,
              color: colors.textSecondary,
              maxWidth: 360,
              margin: "0 auto",
            }}
          >
            Videos will appear here once uploaded — from the Insights recorder,
            Zoom recordings, or external clips.
          </div>
        </div>
      ) : (
        <Table
          columns={columns}
          data={meetings}
          onRowClick={setSelectedMeeting}
        />
      )}

      <MeetingDrawer
        meeting={selectedMeeting}
        onClose={() => setSelectedMeeting(null)}
        onUpdated={(updated) => {
          setMeetings((prev) =>
            prev.map((m) => (m.id === updated.id ? updated : m)),
          );
          setSelectedMeeting(updated);
        }}
        onDeleted={(id) => {
          setMeetings((prev) => prev.filter((m) => m.id !== id));
          setSelectedMeeting(null);
        }}
        userEmail={user?.userDetails}
        isAdmin={role === "admin"}
      />

      <ZoomClipsDrawer
        open={showZoomClips}
        onClose={() => setShowZoomClips(false)}
      />

      <ZoomRecordingsDrawer
        open={showZoomRecordings}
        onClose={() => setShowZoomRecordings(false)}
      />
    </div>
  );
}
